import { useCallback, useEffect, useRef, useState } from 'react'

import { readPersistedTabs, writePersistedTabs } from '../editorTabsStorage'

export interface EditorTab {
  filename: string
  lastScrollBlockId: string | null
}

/** Hard cap on open tabs — opening beyond it silently evicts the least-recently-used tab (IDE-style). */
export const MAX_OPEN_TABS = 6

interface UseEditorTabsArgs {
  bookId?: string
  /** Currently active chapter filename (derived from URL). */
  activeFilename: string | null
  /** All chapter filenames known to exist on the server. Used to drop stale tabs. */
  knownFilenames: string[]
  /** False when tabs feature is disabled (public reading, mobile). */
  enabled: boolean
}

interface CloseTabResult {
  /** Filename to navigate to after close. Null only when the close was rejected (last tab). */
  nextActiveFilename: string | null
  /** True if the close was rejected because it would leave zero tabs. */
  rejected?: boolean
}

export interface UseEditorTabsResult {
  tabs: EditorTab[]
  /**
   * Open a file as a tab. If the file already has a tab, no-op (caller navigates
   * to it). Otherwise insert a new tab right after the active one — evicting the
   * least-recently-used tab beyond the cap. Caller is responsible for navigating.
   */
  openOrActivate: (filename: string) => void
  closeTab: (filename: string) => CloseTabResult
  setScrollAnchor: (filename: string, blockId: string | null) => void
  getScrollAnchor: (filename: string) => string | null
  renameTabFilename: (oldFilename: string, newFilename: string) => void
  /** Move a tab (drag & drop) so it sits at the position currently occupied by `toFilename`. */
  reorderTabs: (fromFilename: string, toFilename: string) => void
  /** Open-tab filenames in most-recently-used order (stable getter, reads a ref). */
  getMruFilenames: () => string[]
}

export function useEditorTabs({
  bookId,
  activeFilename,
  knownFilenames,
  enabled,
}: UseEditorTabsArgs): UseEditorTabsResult {
  const [tabs, setTabs] = useState<EditorTab[]>([])
  const tabsRef = useRef<EditorTab[]>(tabs)
  tabsRef.current = tabs
  const restoredRef = useRef(false)
  // Always reflects the current activeFilename (synced during render so handlers read fresh).
  const activeFilenameRef = useRef(activeFilename)
  activeFilenameRef.current = activeFilename
  // Reflects the active filename as seen on the previous render. Used by the safety-net
  // effect to know which tab slot to replace when the URL changes to a file that has no tab yet.
  const previousActiveRef = useRef<string | null>(activeFilename)

  // Most-recently-used filenames, current first. Drives LRU eviction at the tab
  // cap and the ⌥Tab switcher's ordering.
  const mruRef = useRef<string[]>([])
  useEffect(() => {
    if (!enabled || !activeFilename) {
      return
    }
    mruRef.current = [activeFilename, ...mruRef.current.filter((f) => f !== activeFilename)]
  }, [activeFilename, enabled])

  const getMruFilenames = useCallback(() => mruRef.current, [])

  /**
   * Trim `list` down to MAX_OPEN_TABS by evicting least-recently-used tabs.
   * Never evicts `keepFilename` or the currently active tab.
   */
  const enforceTabLimit = useCallback((list: EditorTab[], keepFilename: string): EditorTab[] => {
    let next = list
    while (next.length > MAX_OPEN_TABS) {
      const mru = mruRef.current
      let evictIdx = -1
      let evictRecency = -1
      next.forEach((tab, i) => {
        if (tab.filename === keepFilename || tab.filename === activeFilenameRef.current) {
          return
        }
        const mruIdx = mru.indexOf(tab.filename)
        // Never-visited tabs are the stalest of all.
        const recency = mruIdx === -1 ? Number.MAX_SAFE_INTEGER : mruIdx
        if (recency > evictRecency) {
          evictRecency = recency
          evictIdx = i
        }
      })
      if (evictIdx < 0) {
        break
      }
      next = next.filter((_, i) => i !== evictIdx)
    }
    return next
  }, [])

  // Restore persisted tabs once `knownFilenames` is populated. Filter out tabs
  // whose source files no longer exist (deleted on another device). If the
  // active filename isn't in the persisted list, append it so the active tab
  // is always present (invariant: at least one tab and it includes active).
  useEffect(() => {
    if (!enabled || restoredRef.current || !bookId || knownFilenames.length === 0) {
      return
    }
    restoredRef.current = true
    const persisted = readPersistedTabs(bookId)
    if (!persisted) {
      return
    }
    const known = new Set(knownFilenames)
    // Recency outlives the reload, or ⌥Tab would degrade to tab-bar order and
    // the first tap would land somewhere arbitrary. Anything already visited
    // this session (the active file) stays ahead of the restored order.
    mruRef.current = [
      ...mruRef.current,
      ...(persisted.mru ?? []).filter((f) => known.has(f) && !mruRef.current.includes(f)),
    ]
    let restored: EditorTab[] = persisted.filenames
      .filter((f) => known.has(f))
      .map((f) => ({ filename: f, lastScrollBlockId: null }))
    if (activeFilename && known.has(activeFilename) && !restored.some((t) => t.filename === activeFilename)) {
      restored.push({ filename: activeFilename, lastScrollBlockId: null })
    }
    // Cap restored tabs too (the persisted list may predate the limit). Keep the
    // active tab even when it fell beyond the cut.
    if (restored.length > MAX_OPEN_TABS) {
      const active = restored.find((t) => t.filename === activeFilename)
      restored = restored.slice(0, MAX_OPEN_TABS)
      if (active && !restored.some((t) => t.filename === active.filename)) {
        restored[restored.length - 1] = active
      }
    }
    if (restored.length > 0) {
      setTabs(restored)
    }
  }, [bookId, knownFilenames, enabled, activeFilename])

  // Safety net: when activeFilename changes to a file that has no tab (URL bar,
  // browser back/forward, search-result clicks), replace the previously-active
  // tab's slot with the new file.
  //
  // IMPORTANT: depends only on `activeFilename`, not `tabs`. If we depended on
  // `tabs`, an `openOrActivate` call (which mutates tabs before the URL
  // navigation completes) would trigger this effect while activeFilename still
  // points at the previous file, mis-assigning tab slots.
  useEffect(() => {
    if (!enabled || !activeFilename) {
      previousActiveRef.current = activeFilename ?? null
      return
    }
    if (tabsRef.current.some((t) => t.filename === activeFilename)) {
      previousActiveRef.current = activeFilename
      return
    }
    const prevActive = previousActiveRef.current
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.filename === prevActive)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = { filename: activeFilename, lastScrollBlockId: null }
        return next
      }
      return enforceTabLimit([...prev, { filename: activeFilename, lastScrollBlockId: null }], activeFilename)
    })
    previousActiveRef.current = activeFilename
  }, [activeFilename, enabled, enforceTabLimit])

  // Drop tabs whose underlying chapter was deleted.
  useEffect(() => {
    if (!enabled || !restoredRef.current) {
      return
    }
    const known = new Set(knownFilenames)
    setTabs((prev) => {
      const filtered = prev.filter((t) => known.has(t.filename))
      if (filtered.length === prev.length) {
        return prev
      }
      return filtered
    })
  }, [knownFilenames, enabled])

  // Persist on every change (post-restore).
  useEffect(() => {
    if (!enabled || !bookId || !restoredRef.current) {
      return
    }
    const open = new Set(tabs.map((t) => t.filename))
    writePersistedTabs(bookId, {
      filenames: tabs.map((t) => t.filename),
      activeFilename: activeFilename ?? undefined,
      mru: mruRef.current.filter((f) => open.has(f)),
    })
  }, [tabs, activeFilename, bookId, enabled])

  const openOrActivate = useCallback(
    (filename: string) => {
      setTabs((prev) => {
        if (prev.some((t) => t.filename === filename)) {
          return prev
        }
        const activeIdx = prev.findIndex((t) => t.filename === activeFilenameRef.current)
        const newTab: EditorTab = { filename, lastScrollBlockId: null }
        if (activeIdx < 0) {
          return enforceTabLimit([...prev, newTab], filename)
        }
        const next = [...prev]
        next.splice(activeIdx + 1, 0, newTab)
        return enforceTabLimit(next, filename)
      })
    },
    [enforceTabLimit],
  )

  const closeTab = useCallback((filename: string): CloseTabResult => {
    const current = tabsRef.current
    const idx = current.findIndex((t) => t.filename === filename)
    if (idx < 0) {
      return { nextActiveFilename: activeFilenameRef.current }
    }
    // Invariant: always keep at least one tab open.
    if (current.length <= 1) {
      return { nextActiveFilename: activeFilenameRef.current, rejected: true }
    }
    const next = current.filter((t) => t.filename !== filename)
    setTabs(next)

    if (filename !== activeFilenameRef.current) {
      return { nextActiveFilename: activeFilenameRef.current }
    }
    const target = idx < next.length ? next[idx] : next[next.length - 1]
    return { nextActiveFilename: target.filename }
  }, [])

  const setScrollAnchor = useCallback((filename: string, blockId: string | null) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.filename === filename)
      if (idx < 0 || prev[idx].lastScrollBlockId === blockId) {
        return prev
      }
      const next = [...prev]
      next[idx] = { ...next[idx], lastScrollBlockId: blockId }
      return next
    })
  }, [])

  const getScrollAnchor = useCallback((filename: string): string | null => {
    return tabsRef.current.find((t) => t.filename === filename)?.lastScrollBlockId ?? null
  }, [])

  const renameTabFilename = useCallback((oldFilename: string, newFilename: string) => {
    if (oldFilename === newFilename) {
      return
    }
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.filename === oldFilename)
      if (idx < 0) {
        return prev
      }
      const next = [...prev]
      next[idx] = { ...next[idx], filename: newFilename }
      return next
    })
    mruRef.current = mruRef.current.map((f) => (f === oldFilename ? newFilename : f))
  }, [])

  const reorderTabs = useCallback((fromFilename: string, toFilename: string) => {
    setTabs((prev) => {
      const from = prev.findIndex((t) => t.filename === fromFilename)
      const to = prev.findIndex((t) => t.filename === toFilename)
      if (from < 0 || to < 0 || from === to) {
        return prev
      }
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }, [])

  return {
    tabs,
    openOrActivate,
    closeTab,
    setScrollAnchor,
    getScrollAnchor,
    renameTabFilename,
    reorderTabs,
    getMruFilenames,
  }
}
